// NOTIFPREF1 (docs/build-briefs/NOTIFPREF1-notification-settings.md; design/style-guide.md sections 3, 7, 10, 11, 12;
// design/mockups/entbasics/notifpref1-notification-settings.html). The Notifications page, one component for the location,
// franchisor and Snip & Sip Admin consoles. Each console hands it a `read` and a `save` over its own session and a Back action;
// the page draws what the server sent: optional groups as cards with a switch that saves at once and shows "Saved", then one
// "Always sent" card whose rows are locked. The server decides what is optional, what each console offers and what the default
// is (src/domain/emailCatalog.ts); this file holds none of that. Text is set with textContent only.

import { createFailureState } from "./failureState.ts";
import { hoverHelp } from "./hoverHelp.ts";

export interface NotificationRowDto {
  readonly kind: string;
  readonly label: string;
  readonly description: string;
  readonly enabled: boolean;
}
export interface NotificationGroupDto {
  readonly id: string;
  readonly title: string;
  readonly rows: readonly NotificationRowDto[];
}
export interface NotificationLockedDto {
  readonly id: string;
  readonly label: string;
  readonly reason: string;
}
export interface NotificationPageDto {
  readonly email: string | null;
  readonly groups: readonly NotificationGroupDto[];
  readonly locked: readonly NotificationLockedDto[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The page as the server sent it, or undefined when the answer is not the shape this page draws. */
export function parseNotificationPage(value: unknown): NotificationPageDto | undefined {
  if (!isRecord(value)) return undefined;
  const { email, groups, locked } = value;
  if ((email !== null && typeof email !== "string") || !Array.isArray(groups) || !Array.isArray(locked)) return undefined;
  const parsedGroups: NotificationGroupDto[] = [];
  for (const group of groups) {
    if (!isRecord(group) || typeof group["id"] !== "string" || typeof group["title"] !== "string" || !Array.isArray(group["rows"])) return undefined;
    const rows: NotificationRowDto[] = [];
    for (const row of group["rows"]) {
      if (!isRecord(row) || typeof row["kind"] !== "string" || typeof row["label"] !== "string"
        || typeof row["description"] !== "string" || typeof row["enabled"] !== "boolean") return undefined;
      rows.push({ kind: row["kind"], label: row["label"], description: row["description"], enabled: row["enabled"] });
    }
    parsedGroups.push({ id: group["id"], title: group["title"], rows });
  }
  const parsedLocked: NotificationLockedDto[] = [];
  for (const row of locked) {
    if (!isRecord(row) || typeof row["id"] !== "string" || typeof row["label"] !== "string" || typeof row["reason"] !== "string") return undefined;
    parsedLocked.push({ id: row["id"], label: row["label"], reason: row["reason"] });
  }
  return { email: email as string | null, groups: parsedGroups, locked: parsedLocked };
}

export interface NotificationsTransport {
  read(): Promise<{ status: number; body: unknown }>;
  save(kind: string, enabled: boolean): Promise<{ status: number; body: unknown }>;
}

export interface NotificationsPageOptions {
  readonly transport: NotificationsTransport;
  /** Returns to the screen the person came from. */
  readonly onBack: () => void;
  /** The location console points a person with no address to My Profile. */
  readonly addressHint: string;
  /** A 401 ends the console session; the page does nothing more for it. */
  readonly sessionLost: (status: number) => void;
  /** Prefix for data-testid values, so each console's tests name their own. */
  readonly testPrefix: string;
}

const SAVED_MS = 4000;
const SVG_NS = "http://www.w3.org/2000/svg";
const LOCK_PATH = ["M5 11h14v10H5z", "M8 11V8a4 4 0 0 1 8 0v3"];

function lockIcon(): SVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("class", "notif-lock-icon");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  for (const d of LOCK_PATH) {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export interface NotificationsPage {
  /** Reads the person's page and draws it. */
  load(): Promise<void>;
  /** Clears the page (sign-out, or leaving it). */
  reset(): void;
}

export function createNotificationsPage(host: HTMLElement, options: NotificationsPageOptions): NotificationsPage {
  const { transport, testPrefix } = options;
  let generation = 0;
  const timers = new Set<number>();

  const clearTimers = (): void => {
    for (const timer of timers) window.clearTimeout(timer);
    timers.clear();
  };

  const reset = (): void => {
    generation += 1;
    clearTimers();
    host.replaceChildren();
  };

  const head = (): HTMLElement => {
    const back = el("button", "btn-secondary btn-compact notif-back", "‹ Back");
    back.type = "button";
    back.dataset["testid"] = `${testPrefix}-back`;
    back.addEventListener("click", options.onBack);
    return back;
  };

  const showFailure = (sentence: string, retry: () => void): void => {
    host.replaceChildren(head(), createFailureState({ sentence, onRetry: retry, testid: `${testPrefix}-failed` }));
  };

  const row = (dto: NotificationRowDto, myGeneration: number): HTMLElement => {
    const line = el("div", "notif-row");
    line.dataset["testid"] = `${testPrefix}-row-${dto.kind}`;
    const text = el("div", "notif-text");
    text.append(el("b", undefined, dto.label), el("span", undefined, dto.description));
    const saved = el("span", "notif-saved", "✓ Saved");
    saved.hidden = true;
    saved.setAttribute("role", "status");
    saved.dataset["testid"] = `${testPrefix}-saved-${dto.kind}`;
    const problem = el("span", "field-error notif-problem");
    problem.setAttribute("role", "alert");
    problem.dataset["testid"] = `${testPrefix}-problem-${dto.kind}`;
    const label = el("label", "admin-toggle-label");
    const input = el("input", "admin-toggle-input");
    input.type = "checkbox";
    input.checked = dto.enabled;
    input.setAttribute("aria-label", dto.label);
    input.dataset["testid"] = `${testPrefix}-switch-${dto.kind}`;
    const track = el("span", "admin-toggle-track");
    const word = el("span", "admin-toggle-status", dto.enabled ? "On" : "Off");
    label.append(input, track, word);
    input.addEventListener("change", () => {
      const wanted = input.checked;
      input.disabled = true;
      problem.textContent = "";
      saved.hidden = true;
      void transport.save(dto.kind, wanted).then((result) => {
        if (myGeneration !== generation) return;
        input.disabled = false;
        if (result.status === 401) {
          options.sessionLost(result.status);
          return;
        }
        if (result.status !== 200) {
          input.checked = !wanted;
          problem.textContent = "Could not save. Try again.";
          return;
        }
        word.textContent = wanted ? "On" : "Off";
        saved.hidden = false;
        const timer = window.setTimeout(() => {
          timers.delete(timer);
          saved.hidden = true;
        }, SAVED_MS);
        timers.add(timer);
      });
    });
    line.append(text, problem, saved, label);
    return line;
  };

  const card = (dto: NotificationGroupDto, myGeneration: number): HTMLElement => {
    const section = el("section", "notif-card");
    section.dataset["testid"] = `${testPrefix}-group-${dto.id}`;
    const title = el("h2", "notif-title", dto.title);
    hoverHelp(title, "Choose which optional emails you get.", "Each switch saves as soon as you tap it.", "Turn off the daily summary if you check sales in the app.");
    section.append(title, ...dto.rows.map((r) => row(r, myGeneration)));
    return section;
  };

  const lockedCard = (rows: readonly NotificationLockedDto[]): HTMLElement => {
    const section = el("section", "notif-card notif-card-locked");
    section.dataset["testid"] = `${testPrefix}-always`;
    section.append(el("h2", "notif-title", "Always sent"), el("p", "notif-help", "These cannot be turned off."));
    const list = el("div", "notif-locked-grid");
    for (const dto of rows) {
      const line = el("div", "notif-row notif-locked");
      line.dataset["testid"] = `${testPrefix}-locked-${dto.id}`;
      const icon = el("span", "notif-lock");
      icon.append(lockIcon());
      const text = el("div", "notif-text");
      text.append(el("b", undefined, dto.label), el("span", undefined, dto.reason));
      line.append(icon, text, el("span", "fl-chip fl-chip-neutral", "Always on"));
      list.append(line);
    }
    section.append(list);
    return section;
  };

  const draw = (page: NotificationPageDto, myGeneration: number): void => {
    const intro = el("p", "notif-sub");
    if (page.email === null) {
      intro.textContent = options.addressHint;
      intro.dataset["testid"] = `${testPrefix}-no-address`;
    } else {
      intro.append("Emails to ", el("b", undefined, page.email), ". These choices are only for you.");
      intro.dataset["testid"] = `${testPrefix}-address`;
    }
    const grid = el("div", "notif-grid");
    grid.append(...page.groups.map((g) => card(g, myGeneration)));
    host.replaceChildren(head(), intro, ...(page.groups.length === 0 ? [] : [grid]), lockedCard(page.locked));
  };

  const load = async (): Promise<void> => {
    reset();
    const myGeneration = generation;
    host.replaceChildren(head(), el("p", "so-state", "Loading notifications..."));
    const result = await transport.read();
    if (myGeneration !== generation) return;
    if (result.status === 401) {
      options.sessionLost(result.status);
      return;
    }
    const page = result.status === 200 ? parseNotificationPage(result.body) : undefined;
    if (page === undefined) {
      showFailure("Notifications did not load.", () => void load());
      return;
    }
    draw(page, myGeneration);
  };

  return { load, reset };
}
